import { useEffect, useRef, useState } from 'react';

import type { Locale } from '../i18n/locales';
import { LIST_BUCKET, articlesIndexSeedKey } from '../list-queries';
import { sanityClient } from '../sanity/client';
import { articlesQuery, type Article } from '../sanity/queries';
import { getPrefetched } from '../static-prefetch';

/**
 * Mirrors use-events.ts/use-tours.ts: reads the locale's build-time seed (see
 * the app's src/lib/detail/list-prefetch.ts) first, so the homepage carousel and the
 * /[lang]/articles hub ship real article links in the static HTML instead of
 * an empty list until hydration. The live fetch then keeps it fresh.
 *
 * Unlike events/tours there's no filtered variant of this query — it's always
 * "every article in this locale" — so the seed applies unconditionally rather
 * than only for one particular call site's default view.
 */
export function useArticles(locale: Locale) {
  const seedKey = articlesIndexSeedKey(locale);
  const [seed] = useState(() => getPrefetched<Article[]>(LIST_BUCKET, seedKey));
  const [articles, setArticles] = useState<Article[]>(seed ?? []);
  const [loading, setLoading] = useState(seed === undefined);
  const [error, setError] = useState<Error | null>(null);

  // See use-events.ts — suppresses the loading flash only for the seeded
  // locale's first run.
  const seededLocale = useRef(seed !== undefined ? locale : null);

  useEffect(() => {
    let cancelled = false;
    const isSeededFirstRun = seededLocale.current === locale;
    seededLocale.current = null;

    // A seeded list is already complete and no staler than the static HTML
    // around it — both come from the same daily build. Refetching it on load
    // would pull the same rows over the network a second time for no visible
    // change.
    if (isSeededFirstRun) return;

    setLoading(true);
    setError(null);

    sanityClient
      .fetch<Article[]>(articlesQuery, { lang: locale })
      .then((data) => {
        if (!cancelled) setArticles(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  return { articles, loading, error };
}
